/**
 * What the connection attempt actually managed, in words, for when it fails.
 *
 * "No direct channel within the time allowed" says nothing about where it
 * stopped, and the three places it can stop need three different fixes:
 * no relay address means the relay refused or was unreachable, a relay
 * address plus a stuck check means the two ends could not pair their
 * addresses, and no addresses at all means the browser never got going.
 *
 * Written down here rather than read off a phone screen in a chat message,
 * because the phone is where this fails and the phone has no console.
 */

/** The address kinds a browser can gather, plainest word first. */
const WORDS = {
  host: "this network",
  srflx: "public address",
  prflx: "public address",
  relay: "relay",
};

/**
 * Watches an attempt without taking part in it.
 *
 * Uses addEventListener rather than the onicecandidate property, because
 * that property belongs to the handshake and assigning it here would quietly
 * break the connection this is meant to describe.
 */
export function watchAttempt(peerConnection) {
  const kinds = [];
  const states = [];
  let answered = false;

  const noteCandidate = (event) => {
    const kind = event?.candidate?.type;
    if (kind && !kinds.includes(kind)) kinds.push(kind);
  };
  const noteState = () => {
    const state = peerConnection.iceConnectionState;
    if (state && states[states.length - 1] !== state) states.push(state);
  };
  // "stable" after an offer or an answer means the other device's half is in
  // place. Without this, a connection that never heard back and one that
  // heard back and could not pair look identical from the outside, and they
  // are different problems.
  const noteSignalling = () => {
    if (peerConnection.signalingState === "stable") answered = true;
  };

  peerConnection.addEventListener?.("icecandidate", noteCandidate);
  peerConnection.addEventListener?.("iceconnectionstatechange", noteState);
  peerConnection.addEventListener?.("signalingstatechange", noteSignalling);

  return {
    get kinds() {
      return [...kinds];
    },
    get states() {
      return [...states];
    },
    get answered() {
      return answered;
    },
    /** One sentence a person can read, and paste back. */
    summary() {
      const heard = answered
        ? "The other device answered."
        : "The other device never answered.";
      if (!kinds.length) return `This device found no addresses at all to try. ${heard}`;
      const found = kinds.map((k) => WORDS[k] || k).filter((w, i, all) => all.indexOf(w) === i);
      const reached = states.length ? states[states.length - 1] : "nothing";
      const relay = kinds.includes("relay")
        ? "The relay did give this device an address."
        : "The relay gave this device no address.";
      return `Found: ${found.join(", ")}. ${relay} ${heard} Got as far as: ${reached}.`;
    },
  };
}
